// Vidéos d'aide greffées sur les guides (guides.json).
// Chaque guide peut porter, en plus de ses PDF :
//   - icon     : un emoji affiché sur la tuile de /help/videos
//   - keywords : mots-clés libres, toutes langues, séparés par des virgules
//   - videos   : [{ title, youtube }] — liens YouTube « non répertoriés »
// Partagé par la page publique (/help/videos), la page guide (/guides/:id)
// et l'admin (Gestion infos).

export const GUIDES_ENDPOINTS = (origin) => [
  `${origin}/api/guides/config.php`,
  'https://villa-santeodoro.com/api/guides/config.php'
];

// Charge guides.json via l'API (même logique de repli que GestionInfos).
export const fetchGuides = async (origin) => {
  let lastError = null;
  for (const endpoint of GUIDES_ENDPOINTS(origin)) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(endpoint, { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.toLowerCase().includes('application/json')) {
        throw new Error('Réponse non JSON');
      }
      // eslint-disable-next-line no-await-in-loop
      const data = await response.json();
      return { guides: data.guides || [], baseUrl: new URL(endpoint).origin };
    } catch (err) {
      lastError = err;
    }
  }
  throw lastError || new Error('Impossible de charger les guides');
};

// Accepte youtu.be/ID, youtube.com/watch?v=ID, /shorts/ID, /embed/ID ou l'ID seul.
export const youtubeId = (value) => {
  const raw = String(value || '').trim();
  if (/^[\w-]{11}$/.test(raw)) {
    return raw;
  }
  const match = raw.match(/(?:youtu\.be\/|[?&]v=|\/shorts\/|\/embed\/|\/live\/)([\w-]{11})/);
  return match ? match[1] : '';
};

export const youtubeThumbnail = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

export const youtubeEmbedUrl = (id) =>
  `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&playsinline=1`;

export const guideVideos = (guide) =>
  (Array.isArray(guide?.videos) ? guide.videos : []).filter((video) => youtubeId(video.youtube));

export const guideHasPdf = (guide) => Object.values(guide?.languages || {}).some(Boolean);

// Minuscules, sans accents ni ponctuation : « Problème d'eau » → « probleme d eau ».
export const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();

// Mots trop courants pour compter dans une recherche.
const STOP_WORDS = new Set([
  'le', 'la', 'les', 'de', 'des', 'du', 'un', 'une', 'd', 'l', 'j', 'je', 'il', 'y', 'a', 'en', 'et', 'pas', 'plus', 'ne', 'n',
  'il', 'lo', 'gli', 'di', 'e', 'non', 'che', 'una', 'uno',
  'the', 'a', 'an', 'of', 'to', 'is', 'no', 'not', 'my', 'how', 'there',
  'der', 'die', 'das', 'ist', 'kein', 'keine', 'nicht', 'el', 'los', 'las', 'hay', 'o', 'os', 'do', 'da', 'nao', 'sem',
  // Mots « génériques » qui ne disent rien du sujet
  'probleme', 'problem', 'problema', 'panne', 'comment', 'come', 'aide', 'aiuto', 'help', 'hilfe', 'ayuda', 'ajuda'
]);

const queryTokens = (query) =>
  normalizeText(query)
    .split(' ')
    .filter((token) => token.length > 1 && !STOP_WORDS.has(token));

// Distance d'édition bornée : tolère une faute de frappe (« electicite »).
const withinOneEdit = (a, b) => {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
    } else {
      edits += 1;
      if (edits > 1) return false;
      if (a.length > b.length) i += 1;
      else if (b.length > a.length) j += 1;
      else {
        i += 1;
        j += 1;
      }
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
};

// Score d'un mot de la requête contre la liste de mots d'un texte.
const tokenScore = (token, words) => {
  let best = 0;
  for (const word of words) {
    if (word === token) return 3;
    if (word.startsWith(token) || (token.length >= 4 && token.startsWith(word) && word.length >= 4)) {
      best = Math.max(best, 2);
    } else if (token.length >= 4 && withinOneEdit(token, word.slice(0, token.length + 1))) {
      best = Math.max(best, 1);
    } else if (token.length >= 4 && withinOneEdit(token, word)) {
      best = Math.max(best, 1);
    }
  }
  return best;
};

// Recherche dans les guides : titre, mots-clés et titres des vidéos.
// Renvoie les guides triés par pertinence (un guide qui correspond à plus de
// mots de la requête passe devant).
export const searchGuides = (guides, query) => {
  const tokens = queryTokens(query);
  if (tokens.length === 0) {
    return [];
  }

  const scored = guides.map((guide) => {
    const titleWords = normalizeText(guide.label).split(' ');
    const keywordWords = normalizeText(guide.keywords).split(' ');
    const videoWords = normalizeText(guideVideos(guide).map((video) => video.title).join(' ')).split(' ');

    let score = 0;
    for (const token of tokens) {
      const tokenBest = Math.max(
        tokenScore(token, titleWords) * 2,
        tokenScore(token, keywordWords) * 2,
        tokenScore(token, videoWords)
      );
      score += tokenBest;
    }
    return { guide, score };
  });

  return scored
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((entry) => entry.guide);
};
